import type { FlowApprovalSubmission } from '@reflex/flows';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import {
  Bot,
  GitFork,
  Sparkles,
  ShieldCheck,
  Play,
  PlayCircle,
  Zap,
  ChevronRight,
  ChevronDown,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  Mail,
  GitPullRequest,
  GitBranch,
  CircleDot,
  Clock,
  Bug,
  ArrowRight,
  Cog,
} from 'lucide-react';
import { CardGrid } from '@reflex/ui/components/shared/CardGrid';
import { JsonSyntax } from '@reflex/ui/components/content/log-viewer';
import { MarkdownContent } from '@reflex/ui/components/content/MarkdownContent';
import { SectionLabel } from '@reflex/ui/components/layout/Typography';
import { Spinner } from '@reflex/ui/components/shared/Spinner';
import { OwnerChip } from '@reflex/ui/components/ownership/OwnerChip';
import { ResourceChip } from '@reflex/ui/components/resource-preview/ResourceChip';
import { cn } from '@reflex/ui/lib/utils';
import { formatDurationRange } from '@reflex/ui/lib/format';
import { usePlugins } from '@reflex/ui/client/hooks';
import { isRunTerminal } from '@reflex/flows';
import type {
  FlowDefinition,
  FlowNode,
  FlowRun,
  FlowRunNode,
  FlowTriggerNodeConfig,
} from '@reflex/flows';
import { FlowStatusBadge } from './FlowStatusBadge';
import { FlowApprovalCard } from './FlowApprovalCard';
import {
  buildChatBubbles,
  resolveStructuredPayload,
  type FlowRunChatBubble,
} from './flow-run-chat-utils';
import { StructuredPayloadView } from '@reflex/ui/components/content/StructuredPayloadView';
import { useActionExternalRefs, useTriggerExternalRefs } from '../useExternalRefs';
import { buildExternalRefOverrides, externalRefLabelHints } from './RefChips';

interface FlowRunChatViewProps {
  run: FlowRun;
  flow: FlowDefinition | undefined;
  runNodes: readonly FlowRunNode[] | undefined;
  onOpenAgent: (agentId: string) => void;
  onSubmitApproval: (nodeId: string, response: FlowApprovalSubmission) => void;
  isApprovalSubmitting?: boolean;
}

export function FlowRunChatView({
  run,
  flow,
  runNodes,
  onOpenAgent,
  onSubmitApproval,
  isApprovalSubmitting,
}: FlowRunChatViewProps) {
  // Resolve the trigger node the run was anchored at: the recorded
  // `triggerNodeId` when present, else the graph's sole/first trigger node
  // (legacy runs).
  const triggerConfig = useMemo((): FlowTriggerNodeConfig | undefined => {
    const triggers = (flow?.nodes ?? []).filter(
      (n): n is FlowNode & { type: 'trigger' } => n.type === 'trigger',
    );
    const fired = run.triggerNodeId
      ? triggers.find((n) => n.id === run.triggerNodeId)
      : triggers[0];
    return fired?.config;
  }, [flow?.nodes, run.triggerNodeId]);

  const bubbles = useMemo(
    () =>
      buildChatBubbles({
        run,
        nodes: runNodes,
        triggerConfig,
      }),
    [run, runNodes, triggerConfig],
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const isLive = !isRunTerminal(run.status);

  useEffect(() => {
    if (!autoScroll || !isLive) return;
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [bubbles.length, autoScroll, isLive]);

  return (
    <div
      ref={scrollRef}
      onScroll={(e) => {
        const el = e.currentTarget;
        const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 32;
        setAutoScroll(atBottom);
      }}
      data-lenis-prevent
      className="flex h-full flex-col overflow-y-auto"
    >
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-3 p-4">
        {bubbles.map((bubble) =>
          bubble.kind === 'trigger' ? (
            <TriggerBubble key={bubble.key} bubble={bubble} />
          ) : (
            <NodeBubble
              key={bubble.key}
              node={bubble.node}
              flow={flow}
              run={run}
              onOpenAgent={onOpenAgent}
              onSubmitApproval={onSubmitApproval}
              isApprovalSubmitting={isApprovalSubmitting}
            />
          ),
        )}
        {runNodes && runNodes.length === 0 && !isLive && (
          <p className="py-8 text-center text-xs text-muted-foreground">
            No steps recorded for this run.
          </p>
        )}
      </div>
    </div>
  );
}

// Icon fallbacks for renderer-less triggers, keyed by the renderer-registry
// keys `rendererKeyForTrigger` produces (event names for event triggers).
const TRIGGER_META: Record<string, { icon: React.ComponentType<{ className?: string }> }> = {
  manual: { icon: Play },
  linear_issue: { icon: Mail },
  github_pr: { icon: GitPullRequest },
  github_issue: { icon: CircleDot },
  cron: { icon: Clock },
  sentry_issue: { icon: Bug },
  pylon_issue: { icon: Mail },
  slack_message: { icon: Mail },
};

function TriggerBubble({ bubble }: { bubble: Extract<FlowRunChatBubble, { kind: 'trigger' }> }) {
  const { run, triggerKeys, triggerLabel, triggerConfig } = bubble;
  const { flowTriggerRenderers } = usePlugins();
  const triggerData = run.triggerData as Record<string, unknown> | null;
  const externalRefs = useTriggerExternalRefs(
    triggerConfig?.kind === 'event'
      ? { source: triggerConfig.source, event: triggerConfig.event }
      : undefined,
    triggerData,
  );
  // Service-initiated runs (e.g. the project profiler) get a dedicated
  // layout that attributes the run to its owner and subject resource
  // instead of the generic "Flow triggered with payload" fallback.
  if (triggerConfig?.kind === 'service') {
    return <ServiceTriggerBubble run={run} label={triggerLabel} />;
  }
  // Defensive: `triggerKeys` is normally a non-empty array from
  // `buildChatBubbles`, but guard against malformed/legacy trigger configs so
  // a bad run can never crash the whole run page on `.map(...)`.
  const keys = triggerKeys ?? [];
  const renderer = keys.map((key) => flowTriggerRenderers.get(key)).find((r) => r !== undefined);
  const fallbackMeta =
    keys.map((key) => TRIGGER_META[key]).find((m) => m !== undefined) ?? TRIGGER_META.manual;
  const Icon = renderer?.icon ?? fallbackMeta.icon;
  const label = renderer?.label ?? triggerLabel;
  const hasTriggerData = run.triggerData && Object.keys(run.triggerData).length > 0;
  const RendererComponent = renderer?.component;

  return (
    <article
      data-testid="flow-chat-trigger-bubble"
      className="flex gap-3 rounded-2xl border border-border bg-card p-3"
    >
      <BubbleAvatar>
        <Icon className="h-4 w-4 text-status-accent-indigo" />
      </BubbleAvatar>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium">{label}</span>
          <span className="text-[10px] text-muted-foreground">
            {new Date(run.createdAt).toLocaleString()}
          </span>
        </div>
        {RendererComponent ? (
          <div className="mt-1.5">
            <Suspense fallback={<p className="text-[11px] text-muted-foreground">Loading…</p>}>
              <RendererComponent
                triggerData={triggerData}
                surface="chat"
                externalRefs={externalRefs}
              />
            </Suspense>
          </div>
        ) : (
          <>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              {hasTriggerData
                ? 'Automation triggered with payload'
                : 'Automation triggered with no payload'}
            </p>
            {hasTriggerData && (
              <StructuredPayloadView
                value={run.triggerData as Record<string, unknown>}
                compact
                labelHints={externalRefLabelHints(externalRefs)}
                valueOverrides={buildExternalRefOverrides(externalRefs, triggerData)}
              />
            )}
          </>
        )}
      </div>
    </article>
  );
}

/**
 * Trigger bubble for a service-initiated run. Attributes the run to its
 * owner (via the shared `OwnerChip` popover) and its subject project (via
 * a `ResourceChip` preview popover, mirroring the agent/persona previews),
 * with a little repo context. Reads attribution from `run.triggerData`,
 * which the profiler stamps with `projectId` / `repoSlug` / `repoBranch`.
 */
function ServiceTriggerBubble({ run, label }: { run: FlowRun; label: string }) {
  const triggerData = (run.triggerData ?? {}) as Record<string, unknown>;
  const projectId = typeof triggerData.projectId === 'string' ? triggerData.projectId : null;
  const repoSlug = typeof triggerData.repoSlug === 'string' ? triggerData.repoSlug : null;
  const repoBranch = typeof triggerData.repoBranch === 'string' ? triggerData.repoBranch : null;
  const ownerId = run.userId ?? null;

  return (
    <article
      data-testid="flow-chat-trigger-bubble"
      className="flex gap-3 rounded-2xl border border-border bg-card p-3"
    >
      <BubbleAvatar>
        <Cog className="h-4 w-4 text-status-accent-indigo" />
      </BubbleAvatar>
      <div className="min-w-0 flex-1" data-testid="flow-chat-service-trigger-bubble">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium">{label}</span>
          <span className="text-[10px] text-muted-foreground">
            {new Date(run.createdAt).toLocaleString()}
          </span>
        </div>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          Started automatically by a service.
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <span className="inline-flex items-center gap-1.5">
            <SectionLabel as="span" className="text-[10px]">
              Run by
            </SectionLabel>
            <OwnerChip ownerId={ownerId} />
          </span>
          {projectId && (
            <span className="inline-flex items-center gap-1.5">
              <SectionLabel as="span" className="text-[10px]">
                Project
              </SectionLabel>
              <ResourceChip
                kind="projects"
                id={projectId}
                name={repoSlug ?? undefined}
                data-testid="flow-chat-service-project-chip"
              />
            </span>
          )}
        </div>
        {(repoSlug || repoBranch) && (
          <p className="mt-2 inline-flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground">
            <GitBranch className="h-3 w-3 shrink-0" />
            <span className="truncate">{[repoSlug, repoBranch].filter(Boolean).join(' · ')}</span>
          </p>
        )}
      </div>
    </article>
  );
}

interface NodeBubbleProps {
  node: FlowRunNode;
  flow: FlowDefinition | undefined;
  run: FlowRun;
  onOpenAgent: (agentId: string) => void;
  onSubmitApproval: (nodeId: string, response: FlowApprovalSubmission) => void;
  isApprovalSubmitting?: boolean;
}

function findNodeConfig(flow: FlowDefinition | undefined, nodeId: string): FlowNode | undefined {
  return flow?.nodes.find((n) => n.id === nodeId);
}

function NodeBubble({
  node,
  flow,
  run,
  onOpenAgent,
  onSubmitApproval,
  isApprovalSubmitting,
}: NodeBubbleProps) {
  if (node.nodeType === 'approval' && node.status === 'waiting_approval') {
    const input = (node.input ?? {}) as Record<string, unknown>;
    return (
      <div data-testid={`flow-chat-approval-${node.nodeId}`} className="rounded-2xl">
        <CardGrid cols={2} className="grid-cols-1 sm:grid-cols-1">
          <FlowApprovalCard
            runId={run.id}
            nodeId={node.nodeId}
            nodeName={node.nodeName}
            approvalType={(input.approvalType as string) ?? node.nodeType}
            prompt={input.prompt as string | undefined}
            context={input.context as Record<string, unknown> | null}
            allowFeedback={(input.allowFeedback as boolean) ?? true}
            decisionOptions={
              input.decisionOptions as Array<{ value: string; label: string }> | undefined
            }
            onSubmit={(response) =>
              onSubmitApproval(node.nodeId, { ...response, iteration: node.iteration })
            }
            isSubmitting={isApprovalSubmitting}
          />
        </CardGrid>
      </div>
    );
  }

  const nodeConfig = findNodeConfig(flow, node.nodeId);

  switch (node.nodeType) {
    case 'agent':
      return <AgentNodeBubble node={node} onOpenAgent={onOpenAgent} />;
    case 'agent_task':
      return (
        <AgentTaskNodeBubble
          node={node}
          nodeConfig={nodeConfig?.type === 'agent_task' ? nodeConfig : undefined}
          onOpenAgent={onOpenAgent}
        />
      );
    case 'llm':
      return (
        <LlmNodeBubble
          node={node}
          nodeConfig={nodeConfig?.type === 'llm' ? nodeConfig : undefined}
        />
      );
    case 'router':
      return (
        <RouterNodeBubble
          node={node}
          nodeConfig={nodeConfig?.type === 'router' ? nodeConfig : undefined}
        />
      );
    case 'action':
      return (
        <ActionNodeBubble
          node={node}
          nodeConfig={nodeConfig?.type === 'action' ? nodeConfig : undefined}
        />
      );
    case 'approval':
      return <ApprovalResolvedBubble node={node} />;
    default:
      return null;
  }
}

function AgentNodeBubble({
  node,
  onOpenAgent,
}: {
  node: FlowRunNode;
  onOpenAgent: (agentId: string) => void;
}) {
  const handleClick = () => {
    if (node.agentId) onOpenAgent(node.agentId);
  };
  const output = node.output as Record<string, unknown> | null | undefined;
  const summary = pickFirstString(output, ['summary', 'result', 'description']);
  const prUrl =
    typeof output?.prUrl === 'string'
      ? output.prUrl
      : typeof output?.pullRequestUrl === 'string'
        ? output.pullRequestUrl
        : null;
  const errorMessage =
    node.status === 'error' && typeof output?.error === 'string' ? output.error : null;

  return (
    <BubbleFrame node={node} testid={`flow-chat-agent-${node.nodeId}`}>
      <BubbleAvatar variant="agent">
        <Bot className="h-4 w-4 text-status-info" />
      </BubbleAvatar>
      <div className="min-w-0 flex-1">
        <BubbleHeader node={node} />
        <AgentStatusLine node={node} summary={summary} errorMessage={errorMessage} />
        {prUrl && (
          <a
            href={prUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-1 font-mono text-[11px] text-foreground transition-colors hover:bg-muted"
          >
            <GitPullRequest className="h-3 w-3" />
            <span className="truncate">{prUrl.replace(/^https?:\/\//, '')}</span>
            <ExternalLink className="h-3 w-3 text-muted-foreground" />
          </a>
        )}
        {node.agentId && (
          <button
            type="button"
            onClick={handleClick}
            className={cn(
              'mt-2 inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-medium transition-colors',
              node.status === 'running'
                ? 'border-status-info/30 bg-status-info/10 py-1.5 text-status-info hover:bg-status-info/15'
                : 'border-status-info/20 bg-status-info/5 text-status-info hover:bg-status-info/10',
            )}
          >
            <Bot className={node.status === 'running' ? 'h-3.5 w-3.5' : 'h-3 w-3'} />
            View Agent
            <ExternalLink className="ml-0.5 h-3 w-3" />
          </button>
        )}
      </div>
    </BubbleFrame>
  );
}

function AgentStatusLine({
  node,
  summary,
  errorMessage,
}: {
  node: FlowRunNode;
  summary: string | null;
  errorMessage: string | null;
}) {
  if (errorMessage) {
    return (
      <p className="mt-0.5 inline-flex items-start gap-1.5 text-[11px] text-status-error">
        <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
        <span className="whitespace-pre-wrap">{errorMessage}</span>
      </p>
    );
  }
  if (summary) {
    return (
      <div className="mt-0.5 text-[11px] text-foreground">
        <MarkdownContent content={summary} size="xs" />
      </div>
    );
  }
  const fallback = agentStatusFallback(node);
  if (!fallback) return null;
  return <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{fallback}</p>;
}

function agentStatusFallback(node: FlowRunNode): string {
  switch (node.status) {
    case 'pending':
      return 'Waiting for upstream steps…';
    case 'running':
      return 'Agent is working…';
    case 'completed':
      return 'Completed.';
    case 'error':
      return 'Agent errored.';
    case 'skipped':
      return 'Skipped.';
    default:
      return '';
  }
}

function pickFirstString(
  output: Record<string, unknown> | null | undefined,
  keys: readonly string[],
): string | null {
  if (!output) return null;
  for (const key of keys) {
    const value = output[key];
    if (typeof value === 'string' && value.trim().length > 0) return value;
  }
  return null;
}

function LlmNodeBubble({
  node,
  nodeConfig,
}: {
  node: FlowRunNode;
  nodeConfig?: Extract<FlowNode, { type: 'llm' }>;
}) {
  const output = node.output as Record<string, unknown> | null | undefined;
  const errorMessage =
    node.status === 'error' && typeof output?.error === 'string' ? output.error : null;
  const outputKey = nodeConfig?.config.outputKey ?? 'llmResult';
  const responseFormat = nodeConfig?.config.responseFormat ?? 'text';
  const payload = resolveStructuredPayload(output, outputKey);
  const { text: textPayload, structured: structuredPayload } = normaliseLlmPayload(
    payload,
    responseFormat,
  );

  return (
    <BubbleFrame node={node} testid={`flow-chat-llm-${node.nodeId}`}>
      <BubbleAvatar variant="llm">
        <Sparkles className="h-4 w-4 text-status-accent-purple" />
      </BubbleAvatar>
      <div className="min-w-0 flex-1">
        <BubbleHeader node={node} />
        {node.status === 'running' && (
          <p className="mt-0.5 text-[11px] text-muted-foreground">Thinking…</p>
        )}
        {errorMessage && (
          <p className="mt-0.5 inline-flex items-start gap-1.5 text-[11px] text-status-error">
            <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
            <span className="whitespace-pre-wrap">{errorMessage}</span>
          </p>
        )}
        {!errorMessage && textPayload != null && (
          <MarkdownContent content={textPayload} size="xs" className="mt-1" />
        )}
        {!errorMessage && textPayload == null && structuredPayload != null && (
          <StructuredPayloadView value={structuredPayload} />
        )}
      </div>
    </BubbleFrame>
  );
}

/**
 * Normalise an LLM payload into either a free-form text response or a
 * structured value. When `responseFormat` is `'text'` but the LLM returned a
 * JSON-shaped string under `text` / `result` / etc. (common when the prompt
 * requests JSON without bumping the response format), we parse it so the
 * fields render nicely instead of as a single dense line.
 */
function normaliseLlmPayload(
  payload: unknown,
  responseFormat: 'text' | 'json',
): { text: string | null; structured: unknown } {
  if (responseFormat === 'json') {
    return { text: null, structured: payload };
  }
  if (typeof payload === 'string') {
    return classifyTextResponse(payload);
  }
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    const record = payload as Record<string, unknown>;
    const stringValue = pickFirstString(record, [
      'text',
      'result',
      'response',
      'content',
      'summary',
    ]);
    if (stringValue != null) {
      return classifyTextResponse(stringValue);
    }
    return { text: null, structured: payload };
  }
  return { text: null, structured: payload };
}

/**
 * Decide whether a string LLM response is plain text or a JSON-shaped object/
 * array masquerading as text. JSON arrays/objects route through the structured
 * view; everything else stays as markdown text.
 */
function classifyTextResponse(value: string): { text: string | null; structured: unknown } {
  const trimmed = value.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed != null && typeof parsed === 'object') {
        return { text: null, structured: parsed };
      }
    } catch {
      // Not actually JSON - fall through to text rendering.
    }
  }
  return { text: value, structured: null };
}

function RouterNodeBubble({
  node,
  nodeConfig,
}: {
  node: FlowRunNode;
  nodeConfig?: Extract<FlowNode, { type: 'router' }>;
}) {
  const [expanded, setExpanded] = useState(false);
  const input = node.input as Record<string, unknown> | null | undefined;
  const output = node.output as Record<string, unknown> | null | undefined;
  const errorMessage =
    node.status === 'error' && typeof output?.error === 'string' ? output.error : null;
  const label = node.outputLabel ?? (node.status === 'running' ? 'Deciding…' : null);
  const rationale = pickFirstString(output, ['reasoning', 'explanation', 'rationale']);
  const isLlmStrategy = nodeConfig?.config.strategy === 'llm';

  return (
    <BubbleFrame node={node} testid={`flow-chat-router-${node.nodeId}`}>
      <BubbleAvatar variant="router">
        <GitFork className="h-4 w-4 text-status-accent-teal" />
      </BubbleAvatar>
      <div className="min-w-0 flex-1">
        <BubbleHeader node={node} />
        {errorMessage && (
          <p className="mt-0.5 inline-flex items-start gap-1.5 text-[11px] text-status-error">
            <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
            <span className="whitespace-pre-wrap">{errorMessage}</span>
          </p>
        )}
        {!errorMessage && label && (
          <p className="mt-0.5 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
            Routed to
            <ArrowRight className="h-3 w-3" />
            <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] font-medium text-foreground">
              {label}
            </span>
          </p>
        )}
        {isLlmStrategy && rationale && (
          <p className="mt-1 whitespace-pre-wrap text-[11px] italic text-muted-foreground">
            {rationale}
          </p>
        )}
        {input && Object.keys(input).length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="mt-2 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
          >
            {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            {expanded ? 'Hide routing context' : 'Show routing context'}
          </button>
        )}
        {expanded && input && (
          <pre className="mt-2 max-h-60 overflow-auto rounded border bg-muted/30 p-2 font-mono text-[11px] text-muted-foreground">
            <JsonSyntax value={input} />
          </pre>
        )}
      </div>
    </BubbleFrame>
  );
}

function ApprovalResolvedBubble({ node }: { node: FlowRunNode }) {
  const resolved = node.approvalResponse;
  const input = node.input as Record<string, unknown> | null | undefined;
  const prompt = typeof input?.prompt === 'string' ? input.prompt : null;
  const decisionOptions = (input?.decisionOptions ?? []) as Array<{
    value: string;
    label: string;
  }>;
  const decisionLabel = (() => {
    if (!resolved) return null;
    if (resolved.decision) {
      const match = decisionOptions.find((o) => o.value === resolved.decision);
      if (match) return match.label;
      return formatDecisionFallback(resolved.decision);
    }
    return resolved.approved ? 'Approved' : 'Rejected';
  })();

  return (
    <BubbleFrame node={node} testid={`flow-chat-approval-resolved-${node.nodeId}`}>
      <BubbleAvatar variant="approval">
        <ShieldCheck className="h-4 w-4 text-status-warning" />
      </BubbleAvatar>
      <div className="min-w-0 flex-1">
        <BubbleHeader node={node} />
        {prompt && (
          <p className="mt-0.5 line-clamp-2 text-[11px] text-muted-foreground">{prompt}</p>
        )}
        {resolved ? (
          <div className="mt-1 flex flex-col gap-1 text-xs">
            <div className="inline-flex items-center gap-1.5">
              {resolved.approved ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-status-success" />
              ) : (
                <AlertCircle className="h-3.5 w-3.5 text-status-error" />
              )}
              <span className="font-medium">{decisionLabel}</span>
            </div>
            {resolved.feedback && (
              <p className="whitespace-pre-wrap text-[11px] text-muted-foreground">
                {resolved.feedback}
              </p>
            )}
          </div>
        ) : (
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {node.status === 'skipped' ? 'Approval skipped.' : 'Awaiting response.'}
          </p>
        )}
      </div>
    </BubbleFrame>
  );
}

function formatDecisionFallback(decision: string): string {
  return decision.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function AgentTaskNodeBubble({
  node,
  nodeConfig,
  onOpenAgent,
}: {
  node: FlowRunNode;
  nodeConfig?: Extract<FlowNode, { type: 'agent_task' }>;
  onOpenAgent: (agentId: string) => void;
}) {
  const handleClick = () => {
    if (node.agentId) onOpenAgent(node.agentId);
  };
  const output = node.output as Record<string, unknown> | null | undefined;
  const errorMessage =
    node.status === 'error' && typeof output?.error === 'string' ? output.error : null;
  const outputKey = nodeConfig?.config.outputKey ?? 'result';
  const responseFormat = nodeConfig?.config.responseFormat ?? 'text';
  const payload = resolveStructuredPayload(output, outputKey);
  const { text: textPayload, structured: structuredPayload } = normaliseLlmPayload(
    payload,
    responseFormat,
  );
  const labelHints = extractSchemaLabelHints(nodeConfig?.config.jsonSchema);

  return (
    <BubbleFrame node={node} testid={`flow-chat-agent-task-${node.nodeId}`}>
      <BubbleAvatar variant="agent">
        <PlayCircle className="h-4 w-4 text-status-accent-sky" />
      </BubbleAvatar>
      <div className="min-w-0 flex-1">
        <BubbleHeader node={node} />
        {node.status === 'running' && (
          <p className="mt-0.5 text-[11px] text-muted-foreground">Running task…</p>
        )}
        {errorMessage && (
          <p className="mt-0.5 inline-flex items-start gap-1.5 text-[11px] text-status-error">
            <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
            <span className="whitespace-pre-wrap">{errorMessage}</span>
          </p>
        )}
        {!errorMessage && textPayload != null && (
          <MarkdownContent content={textPayload} size="xs" className="mt-1" />
        )}
        {!errorMessage && textPayload == null && structuredPayload != null && (
          <StructuredPayloadView value={structuredPayload} labelHints={labelHints} />
        )}
        {node.agentId && (
          <button
            type="button"
            onClick={handleClick}
            className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-status-accent-sky/20 bg-status-accent-sky/5 px-2.5 py-1 text-[11px] font-medium text-status-accent-sky transition-colors hover:bg-status-accent-sky/10"
          >
            <Bot className="h-3 w-3" />
            View Agent
            <ExternalLink className="ml-0.5 h-3 w-3" />
          </button>
        )}
      </div>
    </BubbleFrame>
  );
}

function extractSchemaLabelHints(
  schema: Record<string, unknown> | undefined,
): Record<string, string> | undefined {
  if (!schema || typeof schema !== 'object') return undefined;
  const properties = schema.properties as Record<string, unknown> | undefined;
  if (!properties) return undefined;
  const hints: Record<string, string> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value && typeof value === 'object' && 'title' in value) {
      const title = (value as { title?: unknown }).title;
      if (typeof title === 'string' && title.length > 0) {
        hints[key] = title;
      }
    }
  }
  return Object.keys(hints).length > 0 ? hints : undefined;
}

function ActionNodeBubble({
  node,
  nodeConfig,
}: {
  node: FlowRunNode;
  nodeConfig?: Extract<FlowNode, { type: 'action' }>;
}) {
  const output = node.output as Record<string, unknown> | null | undefined;
  const input = node.input as Record<string, unknown> | null | undefined;
  const errorMessage =
    node.status === 'error' && typeof output?.error === 'string' ? output.error : null;
  const provider = nodeConfig?.config.provider;
  const operation = nodeConfig?.config.operation;
  // Prefer the RESOLVED params the engine persisted on the run node over
  // the config's raw templates ('{{trigger.channelId}}') — the run view
  // shows what actually ran, and only concrete ids can badge.
  const params = (input?.params ?? nodeConfig?.config.params ?? input) as
    | Record<string, unknown>
    | undefined;
  const result = output && typeof output === 'object' ? output : null;
  const { params: paramRefs, result: resultRefs } = useActionExternalRefs(
    provider && operation ? { provider, operation } : undefined,
    params ?? null,
    result,
  );

  return (
    <BubbleFrame node={node} testid={`flow-chat-action-${node.nodeId}`}>
      <BubbleAvatar variant="action">
        <Zap className="h-4 w-4 text-status-warning" />
      </BubbleAvatar>
      <div className="min-w-0 flex-1">
        <BubbleHeader node={node} />
        {provider && operation && (
          <p className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-muted-foreground">
            <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] font-medium text-foreground">
              {provider}.{operation}
            </span>
          </p>
        )}
        {node.status === 'running' && (
          <p className="mt-1 text-[11px] text-muted-foreground">Executing action…</p>
        )}
        {errorMessage && (
          <p className="mt-1 inline-flex items-start gap-1.5 text-[11px] text-status-error">
            <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
            <span className="whitespace-pre-wrap">{errorMessage}</span>
          </p>
        )}
        {node.status === 'completed' && !errorMessage && (
          <p className="mt-1 inline-flex items-center gap-1.5 text-[11px] text-status-success">
            <CheckCircle2 className="h-3.5 w-3.5" />
            <span>Action succeeded</span>
          </p>
        )}
        {node.status === 'running' && params && Object.keys(params).length > 0 && (
          <div className="mt-1">
            <SectionLabel as="span" className="text-[10px]">
              Params
            </SectionLabel>
            <StructuredPayloadView
              value={params}
              labelHints={externalRefLabelHints(paramRefs)}
              valueOverrides={buildExternalRefOverrides(paramRefs, params)}
            />
          </div>
        )}
        {!errorMessage && result && Object.keys(result).length > 0 && (
          <div className="mt-1">
            <SectionLabel as="span" className="text-[10px]">
              Result
            </SectionLabel>
            <StructuredPayloadView
              value={result}
              labelHints={externalRefLabelHints(resultRefs)}
              valueOverrides={buildExternalRefOverrides(resultRefs, result)}
            />
          </div>
        )}
      </div>
    </BubbleFrame>
  );
}

function BubbleFrame({
  node,
  testid,
  children,
}: {
  node: FlowRunNode;
  testid: string;
  children: React.ReactNode;
}) {
  return (
    <article
      data-testid={testid}
      className={cn(
        'flex gap-3 rounded-2xl border border-border bg-card p-3 transition-colors',
        node.status === 'pending' && 'opacity-70',
        node.status === 'running' && 'animate-[pulse_3s_ease-in-out_infinite]',
      )}
    >
      {children}
    </article>
  );
}

function BubbleAvatar({
  children,
  variant,
}: {
  children: React.ReactNode;
  variant?: 'agent' | 'llm' | 'router' | 'approval' | 'action';
}) {
  void variant;
  return (
    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border bg-background">
      {children}
    </div>
  );
}

function BubbleHeader({ node }: { node: FlowRunNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="truncate text-xs font-medium">{node.nodeName}</span>
      <FlowStatusBadge status={node.status} />
      {node.iteration > 1 && (
        <SectionLabel as="span" className="text-[10px]">
          iter {node.iteration}
        </SectionLabel>
      )}
      {node.status === 'running' && <Spinner size="sm" className="text-status-info" />}
      {node.startedAt && (
        <span className="ml-auto shrink-0 text-[10px] text-muted-foreground tabular-nums">
          {node.completedAt
            ? formatDurationRange(node.startedAt, node.completedAt)
            : formatDurationRange(node.startedAt)}
        </span>
      )}
    </div>
  );
}
